<script lang="ts">
	import { onMount } from 'svelte';
	import { fade } from 'svelte/transition';
	import { theme, type Theme } from './theme';

	let isOpen = $state(false);

	function toggleDropdown() {
		isOpen = !isOpen;
	}

	function selectTheme(newTheme: Theme) {
		$theme = newTheme;
		isOpen = false;
	}

	function handleClickOutside(event: MouseEvent) {
		const target = event.target as HTMLElement;
		if (!target.closest('.theme-toggle')) {
			isOpen = false;
		}
	}

	onMount(() => {
		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	});
</script>

<div class="theme-toggle">
	<button
		class="theme-button"
		type="button"
		onclick={toggleDropdown}
		aria-label="Toggle theme"
		aria-expanded={isOpen}
		aria-haspopup="menu"
	>
		{#if $theme === 'light'}
			<svg
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				stroke-width="2"
				aria-hidden="true"
			>
				<circle cx="12" cy="12" r="5" />
				<line x1="12" y1="1" x2="12" y2="3" />
				<line x1="12" y1="21" x2="12" y2="23" />
				<line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
				<line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
				<line x1="1" y1="12" x2="3" y2="12" />
				<line x1="21" y1="12" x2="23" y2="12" />
				<line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
				<line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
			</svg>
		{:else if $theme === 'dark'}
			<svg
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				stroke-width="2"
				aria-hidden="true"
			>
				<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
			</svg>
		{:else}
			<svg
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				stroke-width="2"
				aria-hidden="true"
			>
				<rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
				<line x1="8" y1="21" x2="16" y2="21" />
				<line x1="12" y1="17" x2="12" y2="21" />
			</svg>
		{/if}
	</button>

	{#if isOpen}
		<div class="theme-dropdown" role="menu" transition:fade={{ duration: 200 }}>
			<button
				class="theme-option"
				class:active={$theme === 'light'}
				type="button"
				role="menuitemradio"
				aria-checked={$theme === 'light'}
				onclick={() => selectTheme('light')}
			>
				<svg
					width="16"
					height="16"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					aria-hidden="true"
				>
					<circle cx="12" cy="12" r="5" />
					<line x1="12" y1="1" x2="12" y2="3" />
					<line x1="12" y1="21" x2="12" y2="23" />
					<line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
					<line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
					<line x1="1" y1="12" x2="3" y2="12" />
					<line x1="21" y1="12" x2="23" y2="12" />
					<line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
					<line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
				</svg>
				Light
			</button>
			<button
				class="theme-option"
				class:active={$theme === 'dark'}
				type="button"
				role="menuitemradio"
				aria-checked={$theme === 'dark'}
				onclick={() => selectTheme('dark')}
			>
				<svg
					width="16"
					height="16"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					aria-hidden="true"
				>
					<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
				</svg>
				Dark
			</button>
			<button
				class="theme-option"
				class:active={$theme === 'system'}
				type="button"
				role="menuitemradio"
				aria-checked={$theme === 'system'}
				onclick={() => selectTheme('system')}
			>
				<svg
					width="16"
					height="16"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					aria-hidden="true"
				>
					<rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
					<line x1="8" y1="21" x2="16" y2="21" />
					<line x1="12" y1="17" x2="12" y2="21" />
				</svg>
				System
			</button>
		</div>
	{/if}
</div>

<style>
	.theme-toggle {
		position: relative;
		display: inline-block;
	}

	.theme-button {
		background: var(--bg-elevated);
		border: 1px solid var(--border-strong);
		color: var(--text);
		padding: 0;
		border-radius: var(--radius);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		transition:
			background-color 0.15s ease,
			border-color 0.15s ease;
		width: 2.5rem;
		height: 2.5rem;
	}

	.theme-button:hover,
	.theme-button:focus-visible {
		background: var(--bg-subtle);
		border-color: var(--accent);
	}

	.theme-button:focus-visible {
		outline: 2px solid var(--accent);
		outline-offset: 2px;
	}

	.theme-dropdown {
		position: absolute;
		top: 100%;
		right: 0;
		margin-top: 0.5rem;
		background: var(--bg-elevated);
		border: 1px solid var(--border-strong);
		border-radius: var(--radius);
		box-shadow: 0 4px 12px color-mix(in srgb, var(--bg) 25%, transparent);
		z-index: 1000;
		min-width: 7.5rem;
		overflow: hidden;
	}

	.theme-option {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		width: 100%;
		padding: 0.75rem 1rem;
		background: none;
		border: none;
		color: var(--text);
		cursor: pointer;
		transition: background-color 0.15s ease;
		text-align: left;
		font-size: 0.875rem;
		font-family: var(--font-sans);
	}

	.theme-option:hover,
	.theme-option:focus-visible {
		background: var(--bg-subtle);
	}

	.theme-option.active {
		background: var(--accent-fill);
		color: var(--accent-contrast);
	}

	.theme-option:not(:last-child) {
		border-bottom: 1px solid var(--border);
	}

	@media (prefers-reduced-motion: reduce) {
		.theme-button,
		.theme-option {
			transition-duration: 0.01ms;
		}
	}
</style>
